import { MatchConfig, validateMatchConfig } from "./assignment";
import Cacheable from "./cacheable";
import LocalStore from "./storelocal";
import SessionStore from "./storesession";
import { SessionProfileQueryBinding, SessionProfileAttribute, Loader } from "./session";
import { QueryParser } from "./queryparser";
import { LogLevelEnum, SENTRY_DSN, Telemetry } from "./telemetry";
import Refresher from "./refresher";
import { HttpClient } from "./http";
import {
    ProbeResult,
    attachHttpFailure,
    attachStatusFailure,
    buildFailureReport,
    httpFailureOf,
    markShapeFailure,
    probeControlOrigin,
    stripQuery,
} from "./httpdiagnostics";

declare global {
    interface Window {
        // add support for mocking window elements
        // to simulate source parsing
        __mock_window_search?: string;
        __mock_referrer?: string;
    }
    interface Navigator {
        // Only `deviceMemory` needs augmenting: it's an experimental API that
        // isn't in this TypeScript lib.dom. `hardwareConcurrency`, `language`,
        // `userAgent` and `vendor` are ALREADY declared by lib.dom (as required
        // `number`/`string` via NavigatorConcurrentHardware / NavigatorLanguage
        // / NavigatorID). Re-declaring them here as optional - and `language`
        // as `number` - conflicts with those built-ins and triggers TS2430.
        // Runtime absence (bot-stubbed/missing props) is handled defensively by
        // safeNavigatorString in ./safeglobals, not by weakening these types.
        deviceMemory?: number;
    }
}


interface AnalyticsConfiguration {
    interval: number;
    maxRefreshes: number;
}

// Last-resort defaults used when a config can't be fetched and no cached copy
// exists. They keep the library running (so session/profile data is still
// injected for diagnosis) rather than aborting initialisation. These values
// mirror the inline defaults already used in the constructor / refreshQueries.
const DEFAULT_CONFIGURATION: AnalyticsConfiguration = { interval: 250, maxRefreshes: 5 };
// empty assignments => no lead-source matching applied, but everything else runs
const DEFAULT_LEAD_SOURCE_CONFIGURATION: MatchConfig = { version: 1, assignments: [] };

// Session-scoped cache for the control-probe result, so a client whose config host is
// chronically down does not re-probe on every single page view.
const PROBE_CACHE_KEY = 'fountain-probe-result';
// The only probe results safe to carry across page views. 'failed' is excluded because
// it is the one result the classifier uses as positive evidence, and the one that can go
// stale in a way that misleads. This list is the whitelist for BOTH the write and the
// read, so an unexpected stored value can never be trusted.
const REUSABLE_PROBE_RESULTS: string[] = ['ok', 'blocked', 'skipped'];

// Shape guards: a 200 response can still carry valid JSON of the wrong shape
// (a maintenance/error page served as 200, an array, a primitive). Accepting it
// would cache poison and, for the lead-source config, crash Assigner on every
// refresh. These reject anything that isn't the structure we actually consume.
function isPlainObject(value: any) : boolean {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}
function isValidConfiguration(value: any) : boolean {
    return isPlainObject(value);
}
/**
 * As deep as the server's own check (3.0.0, BACKLOG.md §10): a document that
 * would only half-run (an unknown attribute, a `"false"` string for a flag, a
 * regex the server reads differently) falls back to the cached or default
 * configuration instead.
 */
function isValidLeadSourceConfiguration(value: any) : boolean {
    try {
        validateMatchConfig(value);
        return true;
    } catch (error) {
        console.warn('[fountain-analytics] lead-source configuration rejected:', (error as Error).message);
        return false;
    }
}


export default class Fountain
{

    protected configurationUrl: string;
    protected configuration : AnalyticsConfiguration | null;

    protected queries : Array<SessionProfileQueryBinding>;
    
    protected leadSourceConfigurationUrl: string;
    protected leadSourceConfiguration: MatchConfig | null;

    protected cache: Cacheable;
    protected configCache: LocalStore;
    protected probeCache: SessionStore;
    protected telemetry: Telemetry;

    protected currentRefresh: number;
    protected maxRefreshes: number;

    /** Memoised control-probe result; see probeOnce(). */
    protected probePromise: Promise<ProbeResult> | null;

    /**
     * In-flight failure reports.
     *
     * Reporting is fire-and-forget - see reportDegradation for why - so these handles
     * exist only to keep a rejection from reaching the host page, and to let tests
     * await work that production deliberately does not.
     */
    protected pendingReports: Array<Promise<void>>;


    protected loader: Loader;
    
    /**
     * Fountain Analytics constructor.
     * 
     * @param configuration JSON object with configuration.
     * @param configurationUrl (alternative) URL where JSON object can be fetched.
     * @param leadSourceConfigurationUrl (alternative) URL where lead source JSON object can be fetched.
     * @param leadSourceConfiguration JSON object with lead source configuration.
     * @param lazy (default false) if true, skips initial async call to main()
     */
    constructor(
        configuration: AnalyticsConfiguration | null = null,
        configurationUrl: string | null = null, 
        leadSourceConfigurationUrl: string | null = null,
        leadSourceConfiguration: MatchConfig | null = null,
        lazy: Boolean = false,
    )
    {
        this.configurationUrl = configurationUrl;
        this.leadSourceConfigurationUrl = leadSourceConfigurationUrl;
        this.configuration = configuration;
        this.leadSourceConfiguration = leadSourceConfiguration;
        // store the queries here so that
        // when we refresh on interval we have the query-element
        // relationship bound
        this.queries = [];
        this.appendNewQueries();
        this.cache = new Cacheable();
        this.configCache = new LocalStore();
        this.probeCache = new SessionStore();
        this.loader = new Loader();
        // reset "hitId" on each 'hit' of the website
        // (assumes this library is loaded once per hit)
        this.cache.store.set(SessionProfileAttribute.hitId, []);
        // WARN, not ERROR: the cache-fallback event at fetchJsonConfig() is the
        // denominator. Without it there is no way to tell a config-host outage
        // (a tight time cluster across many client sites) from per-user network
        // flapping (diffuse, few sessions). fetchJsonConfig's WARN call is the
        // only WARN call site, so this enables exactly that event.
        this.telemetry = new Telemetry(LogLevelEnum.WARN, SENTRY_DSN);
        this.probePromise = null;
        this.pendingReports = [];
        this.currentRefresh = 0;
        this.maxRefreshes = DEFAULT_CONFIGURATION.maxRefreshes;
        if (!lazy) {
            setTimeout(this.main.bind(this), 0);
        }
    }

    public async main()
    {
        try {
            await this.doMain();
        } catch (error) {
            await this.telemetry.log(
                LogLevelEnum.CRITICAL,
                error instanceof Error ? error.message : String(error),
                {},
                error instanceof Error ? error : undefined,
            );
        }
        this.telemetry.checkForTest();
    }

    protected async doMain()
    {
        // fetch and set remote configuration, if necessary
        await this.prepareConfiguration();

        // ensure normal query refreshment takes precedence
        this.refreshQueries();

        // then expose an event to allow one-off query refreshment
        // to allow signalling for unexpected DOM structure changes
        window.addEventListener(
            'Fountain.Query.Refresh',
            () => this.safeRefresh(),
        );
    }

    public async prepareConfiguration() : Promise<void>
    {
        if (this.configurationUrl) {
            this.configuration = await this.fetchJsonConfig(
                this.configurationUrl,
                DEFAULT_CONFIGURATION,
                isValidConfiguration,
                'main',
            );
        }
        if (this.configuration === null) {
            // an Error, not a bare string: main()'s `error instanceof Error` check
            // would otherwise fail and the most severe event in the system would
            // arrive with no stack and no extras
            throw new Error('No configuration or invalid configuration provided.');
        }

        if (this.leadSourceConfigurationUrl) {
            this.leadSourceConfiguration = await this.fetchJsonConfig(
                this.leadSourceConfigurationUrl,
                DEFAULT_LEAD_SOURCE_CONFIGURATION,
                isValidLeadSourceConfiguration,
                'lead-source',
            );
        }
        if (this.leadSourceConfiguration === null) {
            throw new Error('No lead source configuration or invalid configuration provided');
        }
    }

    protected async fetchJsonConfig(
        url: string,
        fallback: any,
        validate?: (parsed: any) => boolean,
        configKind: string = 'main',
    )
    {
        const cacheKey = `fountain-config-cache:${url}`;
        // Every URL that reaches Sentry - message text, Error messages, tags,
        // contexts - must be query-stripped: a client's config URL can carry personal
        // data in its query string, and an unstable URL also breaks message grouping.
        // The cache key deliberately keeps the full URL (local only, must stay unique).
        const safeUrl = stripQuery(url);
        try {
            const response = await HttpClient.fetchWithRetry(url);
            // a completed-but-unsuccessful response (e.g. 401/404, which the client
            // returns rather than retries) must be treated as a failure - otherwise
            // a JSON error body would be parsed and cached as if it were config.
            if (!response.ok) {
                const statusError = new Error(
                    `Configuration request for ${safeUrl} returned status ${response.status}`
                );
                // HttpClient RETURNS 404/401 rather than throwing, so no attempt log
                // travels with them. Record the status here or the report would show
                // status 0 and misclassify a conclusive 4xx as a transport failure.
                attachStatusFailure(statusError, url, response);
                throw statusError;
            }
            // parse explicitly: a malformed body (e.g. a truncated response on a
            // flaky connection) must be treated as a failure, not a success, so we
            // fall back to cache rather than caching garbage.
            const raw = response.text;
            let json: any;
            try {
                json = JSON.parse(raw);
            } catch (parseError) {
                // Replace the thrown SyntaxError instead of forwarding it. V8 embeds a
                // prefix of the offending body in `.message` - `Unexpected token '<',
                // "<html><bod"... is not valid JSON` - and that string ships to Sentry
                // as the exception value on the ERROR path and as the event message on
                // the CRITICAL path. Nothing derived from a response body may leave the
                // page (REFERENCE.md §11 invariant 8), and in the 'intercepted' case that
                // body is by definition controlled by whoever intercepted it.
                //
                // `name` is deliberately preserved: isParseFailure matches on
                // 'SyntaxError', and losing it would make the 'intercepted' and
                // 'truncated-body' classes unreachable.
                console.debug(parseError);
                const safeParseError = new Error(
                    `Configuration body from ${safeUrl} is not valid JSON`
                );
                safeParseError.name = 'SyntaxError';
                // Carry the completed response's attempt log onto it. Without that the
                // classifier sees status 0 and bodyLooksHtml false, so 'intercepted' (a
                // captive portal serving HTML) and 'truncated-body' could never be
                // reached - the whole point of capturing that flag.
                attachHttpFailure(safeParseError, {
                    url: safeUrl,
                    attempts: response.attempts || [],
                });
                throw safeParseError;
            }
            // a 200 with valid JSON of the wrong shape is also a failure - reject it
            // before it can be returned or cached.
            if (validate && !validate(json)) {
                const shapeError = new Error(
                    `Configuration from ${safeUrl} did not match the expected shape`
                );
                // distinguishable from a transport failure: we fetched and parsed
                // it fine, our own shape check rejected it
                markShapeFailure(shapeError);
                attachHttpFailure(shapeError, {
                    url: safeUrl,
                    attempts: response.attempts || [],
                });
                throw shapeError;
            }
            // write-through: keep the last known-good config (only what we could
            // actually parse and validate) so a future load on a flaky network can fall back.
            this.configCache.set(cacheKey, [raw]);
            return json;
        } catch (error) {
            const cached = this.readCachedConfig(cacheKey, validate);
            if (cached !== null) {
                // The cache served the user, so this is a recovery - but it is
                // still reported, because the ratio of recoveries to hard failures
                // is what distinguishes a host outage from network flapping.
                //
                // The control probe is deliberately SKIPPED here: it costs up to
                // CONTROL_PROBE_TIMEOUT_MS, and we already hold a usable config.
                // Delaying the page by seconds to enrich a recovery report would
                // trade the user's experience for our diagnostics. Every other
                // signal (failure class, config host, onLine, timing) is present.
                this.startReport(this.telemetry.log(
                    LogLevelEnum.WARN,
                    `Falling back to cached configuration for ${safeUrl}`,
                    {},
                    error instanceof Error ? error : undefined,
                    buildFailureReport(url, error, 'cache', configKind, 'skipped'),
                ));
                return cached;
            }
            // Network and cache both miss: keep running on built-in defaults, and
            // report it - but do NOT wait for the report.
            //
            // There is deliberately no "and if there is no fallback either" branch.
            // A fallback is always supplied - `fallback` is a required parameter, so
            // the compiler enforces it - because a config failure must never stop the
            // session profile being injected. That matters most for the lead-source
            // config, where the failure IS total for attribution: the assignments are
            // empty so no lead source is derived, but the UTMs, click IDs and channel
            // grouping still reach the page, and the lead source can be recovered from
            // them later. Reporting the failure and continuing beats reporting it and
            // taking the recoverable data down too.
            this.startReport(this.reportDegradation(url, error, configKind, safeUrl));
            return fallback;
        }
    }

    /**
     * Assemble and send the report for a config fetch that fell back to defaults.
     *
     * Runs the control probe first, so this can take up to CONTROL_PROBE_TIMEOUT_MS -
     * which is exactly why nothing awaits it. Telemetry must not sit on the critical
     * path: the library's job is to inject the session profile into the page's forms,
     * `refreshQueries` starts that on a short interval (default 250ms x 5), and every
     * millisecond spent here is a millisecond in which a user can reach a form before
     * its hidden fields are populated. Sentry does not care whether an event arrives
     * three seconds later; the visitor does.
     *
     * ERROR => captured, tagged with the client hostname via `client_hostname` and the
     * failure class via `failure_class`, so chronic failures for a given client are
     * visible.
     */
    protected async reportDegradation(
        url: string,
        error: any,
        configKind: string,
        safeUrl: string,
    ) : Promise<void>
    {
        const probe = await this.probeOnce(error);
        await this.telemetry.log(
            LogLevelEnum.ERROR,
            `Configuration fetch failed for ${safeUrl}; using built-in defaults`,
            { url: safeUrl },
            error instanceof Error ? error : undefined,
            buildFailureReport(url, error, 'default', configKind, probe),
        );
    }

    /**
     * Track a fire-and-forget report.
     *
     * Two jobs: swallow any rejection, because an unhandled one from a detached
     * promise still surfaces on the host page (invariant 1), and keep a handle so
     * tests can await work that production deliberately does not.
     */
    protected startReport(work: Promise<unknown>) : void
    {
        this.pendingReports.push(
            Promise.resolve(work).then(
                () : void => undefined,
                (reportError: any) : void => { console.debug(reportError); },
            )
        );
    }

    /**
     * Wait for every in-flight report to finish. Nothing in production calls this -
     * the whole point is not to wait - but a test asserting on telemetry needs to.
     * Drains rather than snapshots, so a report started by another report is included.
     */
    public async settleReports() : Promise<void>
    {
        while (this.pendingReports.length) {
            const batch = this.pendingReports;
            this.pendingReports = [];
            await Promise.all(batch);
        }
    }

    /**
     * Run the control probe at most once per page load, and only when it can tell us
     * something we do not already know.
     *
     * Skipped entirely when a completed HTTP status is in hand: a 404 or 503 already
     * proves the request reached the host, so the probe cannot change the diagnosis
     * and would only add up to CONTROL_PROBE_TIMEOUT_MS to a degraded page load.
     *
     * Memoised because prepareConfiguration fetches two configs sequentially; probing
     * the same origin twice, seconds apart, could add ~6s before the page continues.
     */
    protected probeOnce(error: any) : Promise<ProbeResult>
    {
        const failure = httpFailureOf(error);
        const attempts = failure ? failure.attempts : [];
        const last = attempts.length ? attempts[attempts.length - 1] : null;
        if (last && last.status >= 400) {
            return Promise.resolve<ProbeResult>('skipped');
        }
        if (!this.probePromise) {
            this.probePromise = this.probeForSession();
        }
        return this.probePromise;
    }

    /**
     * The probe result for this SESSION, not this page load.
     *
     * A client whose config host is chronically down would otherwise fire one probe
     * per page view forever, and the answer to "can this page reach our origin?" does
     * not usefully change between two page views seconds apart.
     *
     * A 'failed' result is deliberately NOT cached. It is the only result the
     * classifier uses as positive evidence (rule 9 -> `network-down`), and the only
     * one that can go stale in a way that misleads: a user whose connection recovers
     * mid-session would keep having host outages filed as their own network problem.
     * 'ok', 'blocked' and 'skipped' are safe to reuse - none is positive evidence, so
     * a stale one costs discrimination (falling through to `unknown`) rather than
     * producing a confident wrong answer.
     *
     * Note this cannot help the case it was written for when Web Storage is also
     * unavailable - a blocked-storage client re-probes every load regardless. That is
     * acceptable now that reporting is off the critical path: the cost is one 42-byte
     * image request per page view, not three seconds of the visitor's time.
     */
    protected async probeForSession() : Promise<ProbeResult>
    {
        const cached = this.probeCache.get(PROBE_CACHE_KEY);
        // Validated on READ, not just on write. Session storage is shared with every
        // script on the client's page, so the value can be anything: a stale entry from
        // an older build with a different policy, a key collision, or a page script
        // writing over it. Trusting a stored 'failed' would skip the real probe and
        // then confidently file the config host's own outage as the user's network
        // problem - the invariant has to be enforced where the value is consumed.
        if (cached.length && REUSABLE_PROBE_RESULTS.indexOf(cached[0]) !== -1) {
            return cached[0] as ProbeResult;
        }
        const result = await probeControlOrigin();
        if (REUSABLE_PROBE_RESULTS.indexOf(result) !== -1) {
            this.probeCache.set(PROBE_CACHE_KEY, [result]);
        }
        return result;
    }

    protected readCachedConfig(cacheKey: string, validate?: (parsed: any) => boolean) : any
    {
        const cached = this.configCache.get(cacheKey);
        if (cached.length === 0) {
            return null;
        }
        try {
            const parsed = JSON.parse(cached[0]);
            // reject a cache entry that no longer matches the expected shape
            // (e.g. one written by an older build before shape validation existed).
            if (validate && !validate(parsed)) {
                return null;
            }
            return parsed;
        } catch (error) {
            // a corrupt cache entry should not blow up the fallback path
            return null;
        }
    }

    protected appendNewQueries() : void
    {
        // find additional queries through hidden fields not immediately visible
        let additional = QueryParser.getAll();
        if (additional.length > 0) {
            this.queries = this.queries.concat(additional);
        }
    }

    public doSingleRefresh() : void
    {
        // look for additional hidden inputs to inject values into
        this.appendNewQueries();
        const sessionProfile = this.loader.execute();
        Refresher.execute(this.queries, this.leadSourceConfiguration, sessionProfile);
    }

    protected safeRefresh() : void
    {
        try {
            this.doSingleRefresh();
        } catch (error) {
            this.telemetry.log(
                LogLevelEnum.ERROR,
                error instanceof Error ? error.message : String(error),
                {},
                error instanceof Error ? error : undefined,
            );
        }
    }

    protected refreshQueries() : void
    {
        this.safeRefresh();
        let interval = DEFAULT_CONFIGURATION.interval;
        if (this.configuration.interval) {
            interval = this.configuration.interval;
        }
        if (this.configuration.maxRefreshes) {
            this.maxRefreshes = this.configuration.maxRefreshes;
        }
        if (this.currentRefresh < this.maxRefreshes) {
            // set the next refresh
            setTimeout(
                this.refreshQueries.bind(this),
                interval,
            );
            this.currentRefresh ++;
        }
    }
}